import React, { Fragment, forwardRef } from 'react';
import Type from 'prop-types';
import { groupBy } from 'lodash';
import { useEffectOnce } from 'react-use';
import { AutoSizer, List, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import { Divider, List as MuiList, ListSubheader, Typography } from "@mui/material";
import { useTranslation } from 'hooks';
import InvoiceItem from '../InvoiceItem';
import styles from './InvoiceList.module.css';

const MAX_OVERDUE_DAYS = 30;

const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 200,
});

const InvoiceList = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { invoices: invoicesProp, listHeight } = props;
  const overdueDays = groupBy(invoicesProp, 'overdueDays');
  const sortedOverdueDays = Object.entries(overdueDays).sort((a, b) => {
    return b[0] - a[0];
  });
  const rowCount = sortedOverdueDays.length;

  useEffectOnce(() => {
    cache.clearAll();
  });

  const renderSubheader = (overdueDay) => {
    const color = overdueDay > MAX_OVERDUE_DAYS ? 'primary' : 'default';

    return (
      <ListSubheader component="div">
        {`${t('pdn')}: `}
        <Typography component="span" color={color}>
          {overdueDay}
        </Typography>
      </ListSubheader>
    );
  };

  const rowRenderer = (rowData) => {
    const { index, parent, key, style } = rowData;
    const [overdueDay, invoices] = sortedOverdueDays[index];

    return (
      <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
        <div style={style}>
          <MuiList classes={{ root: styles.list }} subheader={renderSubheader(overdueDay)}>
            {invoices.map((invoice, idx) => {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <Fragment key={idx}>
                  <InvoiceItem invoice={invoice} />

                  {invoices.length - 1 !== idx && <Divider />}
                </Fragment>
              );
            })}
          </MuiList>
        </div>
      </CellMeasurer>
    );
  };

  return (
    <AutoSizer disableHeight>
      {({ width }) => (
        <List
          ref={ref}
          height={listHeight}
          overscanRowCount={2}
          rowCount={rowCount}
          deferredMeasurementCache={cache}
          rowHeight={cache.rowHeight}
          rowRenderer={rowRenderer}
          width={width}
        />
      )}
    </AutoSizer>
  );
});

InvoiceList.propTypes = {
  listHeight: Type.number.isRequired,
  invoices: Type.arrayOf(Type.shape(InvoiceItem.propTypes.operation)).isRequired,
};

export default InvoiceList;
